import React, { useRef, useState } from 'react';
import { Button, Dropdown, Table } from '@ui-modules';
import { cloneDeep, filter, includes, keys, pickBy, sortBy } from 'lodash';
import { ReactComponent as Arrow } from '@assets/svg/arrow-right-circle-fill.svg';
import { Tooltip } from 'antd';
import { ILineItemCurrency, ILineItemsData, ISelectedItems } from '@common/interfaces/Common';
import LineItemModal from '@components/LineItemModal';
import { ReactComponent as EmptyIcon } from '@assets/svg/empty.svg';
import { ReactComponent as SortIcon } from '@assets/svg/sort_line.svg';
import {
  parseDataToTableRows,
  getTableColumns,
  statusDropdownCustomStyles,
  SORT_VARIANTS,
} from './utils';
import './styles.scss';

interface IAssignmentListProps {
  selectedLineItems: ISelectedItems;
  setSelectedLineItems: React.Dispatch<React.SetStateAction<ISelectedItems>>;
  data: ILineItemsData[];
  currencies: ILineItemCurrency;
}

const TABLE_COLUMN_PER_PAGE = 5;
const COLUMN_COUNT_FOR_SCROLLING = 4;

const sortOptions = [{ label: SORT_VARIANTS.LOWEST_OVERALL, value: SORT_VARIANTS.LOWEST_OVERALL }];

const AssignmentList = ({
  selectedLineItems,
  setSelectedLineItems,
  data,
  currencies,
}: IAssignmentListProps) => {
  const [lineItemInfoSelected, setLineItemInfoSelected] = React.useState(null);

  const containerRef = useRef<null | HTMLDivElement>(null);

  const [focusedItem, secFocusedItem] = useState(0);
  const [sorting, setSorting] = useState<null | string>(null);
  const [sortedLine, setSortedLine] = useState<null | number>(null);

  const backButtonClick = (newIndex: number) => {
    if (containerRef.current !== null) {
      const childRef = containerRef.current.childNodes[newIndex] as HTMLDivElement;
      if (childRef) {
        childRef.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
        secFocusedItem(newIndex);
      }
    }
  };

  const onLineItemClick = (lineItemKey: string, index: number): void => {
    const newIndex = index - 1;

    const result = cloneDeep(selectedLineItems);
    if (!result[lineItemKey]) {
      result[lineItemKey] = [];
    }

    keys(pickBy(result, (e) => includes(e, newIndex))).forEach((label) => {
      if (label !== lineItemKey) {
        result[label] = result[label].filter((value) => value !== newIndex);
      }
    });

    if (includes(result[lineItemKey], newIndex)) {
      result[lineItemKey] = filter(result[lineItemKey], (el) => el !== newIndex);
    } else {
      result[lineItemKey] = [...result[lineItemKey], newIndex];
    }

    setSelectedLineItems(cloneDeep(result));
  };

  const preparedData = sortBy(parseDataToTableRows(data), ['order'], ['asc']);

  const preparedColumns = getTableColumns(
    sortBy(data, ['order'], ['asc']),
    currencies,
    selectedLineItems,
    onLineItemClick,
    setLineItemInfoSelected,
    sorting,
    sortedLine,
  );

  const isOverflow = preparedColumns.length > COLUMN_COUNT_FOR_SCROLLING;
  const isLastItemDisabled = focusedItem === preparedColumns.length - TABLE_COLUMN_PER_PAGE;

  return (
    <div className="assignment-list-scroll-wrapper">
      <div className="assignment-list-wrapper last-row-bolded">
        <hr />
        <p className="label">Assign Line items to Provider - Click to select/unselect</p>
        <div className="dropdown-container">
          <Dropdown
            isSearchable={false}
            onChange={(option) => {
              setSortedLine(null);
              setSorting(option.value);
            }}
            options={sortOptions}
            placeholder="Sort quotation by"
            styles={statusDropdownCustomStyles}
            value={
              sorting
                ? {
                    label: sorting,
                    value: sorting,
                  }
                : null
            }
          />
          {sorting && (
            <Button
              className="reset-sorting-btn"
              onClick={() => setSorting(null)}
              text="Reset sorting"
            />
          )}
        </div>
        <div className={`arrow-buttons-container ${!isOverflow && 'hidden'}`}>
          <Arrow
            onClick={() => backButtonClick(focusedItem + 1)}
            className={`scroll-button right ${isLastItemDisabled && 'disabled'}`}
          />
          <Arrow
            onClick={() => backButtonClick(focusedItem - 1)}
            className={`scroll-button left ${focusedItem === 0 && 'disabled'}`}
          />
        </div>
        {preparedData.length > 0 ? (
          <div className="table-wrapper">
            <div className="line-counter">
              <div className="line-counter-header">LINE ITEM</div>
              {preparedData.map(({ id }, index) => {
                const isActive = index === sortedLine;

                return (
                  <div className="line-counter-cell" key={id}>
                    {index < preparedData.length - 1 ? (
                      <>
                        {index + 1}
                        <Tooltip title={isActive ? 'Reset sorting' : 'Sort by lowest'}>
                          <Button
                            className={`sorting-button ${isActive ? 'active' : ''}`}
                            icon={<SortIcon />}
                            onClick={() => {
                              setSorting(null);
                              const value = isActive ? null : index;
                              setSortedLine(value);
                            }}
                          />
                        </Tooltip>
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <Table
              variant="dark"
              columns={preparedColumns}
              data={preparedData}
              scroll={
                isOverflow
                  ? {
                      x:
                        preparedColumns.length *
                        ((window.innerWidth * 0.75 - 180) / TABLE_COLUMN_PER_PAGE),
                    }
                  : undefined
              }
              onRow={(_, index) => {
                if (index === 0) {
                  return {
                    ref: containerRef,
                    onClick: () => null,
                  };
                }
                return {};
              }}
            />
          </div>
        ) : (
          <div className="empty-box">
            <EmptyIcon />
            No data
          </div>
        )}
        {lineItemInfoSelected && (
          <LineItemModal
            lineItemInfoSelected={lineItemInfoSelected}
            setLineItemInfoSelected={setLineItemInfoSelected}
            currencies={currencies}
          />
        )}
      </div>
    </div>
  );
};

export default AssignmentList;
